import React, {useState, useEffect} from 'react';
import {DropdownMenuItem} from '../../ui/dropdown-menu';
import {useLazyQuery, useDomainEvent, useAuth, translate} from '@soap/modules';
import { getPrimaryFunctionAppRoot } from '../../utils/functionAppRoot';
import {Download, X} from 'lucide-react';

interface DownloadStudentDataDialogProps {
    studentId: string;
}

export function DownloadStudentDataDialog({studentId}: DownloadStudentDataDialogProps) {

    const [open, setOpen] = useState(false);

    const [trigger, result] = useLazyQuery('C230v1_GetStudentDataExport');

    const {idToken} = useAuth();

    const triggerCloseContextMenu = useDomainEvent({
        eventName: "close-context-menu",
    });

    useEffect(() => {
        if (open) {
            trigger({
                C230_StudentId: studentId
            });
        }
    }, [open, trigger]);

    const handleDownload = () => {
        if (result?.e184_BlobId) {
            const blobId = result.e184_BlobId;
            const fileName = result.e184_Filename;
            const downloadUrl = `${getPrimaryFunctionAppRoot()}/GetBlob?id=${blobId}&filename=${fileName}&it=${idToken}`;
            window.open(downloadUrl, '_blank');
        }
    };

    const close = () => {
        setOpen(false);
        triggerCloseContextMenu();
    };

    if (!open) {
        return (
            <DropdownMenuItem
                className="w-full justify-start"
                data-dialog-item
                onClick={() => setOpen(true)}
            >
                <Download className="w-4 h-4 mr-2"/>
                {translate("Download Data")}
            </DropdownMenuItem>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-5">
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="text-2xl font-bold text-gray-900">Download Student Data</h2>
                        <button
                            onClick={close}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <div>
                        {!result ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                            </div>
                        ) : result.e184_BlobId ? (
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600">
                                    This is a GDPR-compliant export of all the data in our system for this student.
                                </p>
                                <p className="text-sm text-gray-600">
                                    Your student data export is ready for download.
                                </p>
                                <button
                                    onClick={handleDownload}
                                    className="w-full px-6 py-2 bg-action-blue hover:bg-action-blue-dark text-white rounded-lg transition-colors flex items-center justify-center"
                                >
                                    <Download className="w-4 h-4 mr-2"/>
                                    Download
                                </button>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-600">
                                No data available for download.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
